import { plain } from '../lib/labels';
import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Input, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { adminLinks } from '../lib/adminLinks';
import { listenerLabel, listenerSecondary, userLabel, userSecondary } from '../lib/listenerName';
import { formatRupees } from '../lib/money';

/** One hit from `GET /admin/console/search`. */
export type SearchResult =
  | { type: 'listener'; id: string; displayName: string; handle: string; status: string; gender: string | null }
  /** displayName / userDisplayName = the user's own Display Name — absent on older APIs. */
  | { type: 'user'; id: string; handle: string; displayName?: string | null; phoneLast4: string; listenerId: string | null }
  | {
      type: 'call';
      id: string;
      userHandle: string;
      userDisplayName?: string | null;
      listenerId: string;
      listenerDisplayName: string;
      listenerHandle: string;
      status: string;
      startedAt: string;
    }
  | {
      type: 'recharge';
      id: string;
      userHandle: string;
      userDisplayName?: string | null;
      amountPaise: string;
      status: string;
      gatewayOrderId: string;
    }
  | {
      type: 'payout';
      id: string;
      listenerId: string;
      listenerDisplayName: string;
      listenerHandle: string;
      amountPaise: string;
      status: string;
    };

const TYPE_LABEL: Record<SearchResult['type'], string> = {
  listener: 'Listener',
  user: 'User',
  call: 'Call',
  recharge: 'Recharge',
  payout: 'Payout',
};
const TYPE_COLOR: Record<SearchResult['type'], string> = {
  listener: 'magenta',
  user: 'blue',
  call: 'cyan',
  recharge: 'gold',
  payout: 'green',
};

/** Where a result opens: the entity's own detail page / drawer. */
export function searchResultLink(r: SearchResult): string {
  switch (r.type) {
    case 'listener':
      return adminLinks.listener(r.id);
    case 'user':
      return adminLinks.user(r.id);
    case 'call':
      return adminLinks.call(r.id);
    case 'recharge':
      return adminLinks.recharge(r.id);
    case 'payout':
      return adminLinks.listener(r.listenerId, 'payouts');
  }
}

/** Title + detail line of a result. Listeners always read by Display Name. */
export function searchResultText(r: SearchResult): { title: string; detail: string } {
  switch (r.type) {
    case 'listener':
      return {
        title: listenerLabel(r),
        detail: [listenerSecondary(r), plain(r.status)].filter(Boolean).join(' · '),
      };
    case 'user':
      return {
        title: userLabel(r),
        detail: [userSecondary(r), `••• ${r.phoneLast4}`, r.listenerId ? 'also a listener' : null].filter(Boolean).join(' · '),
      };
    case 'call': {
      const caller = { displayName: r.userDisplayName, handle: r.userHandle };
      return {
        title: `${userLabel(caller)} → ${listenerLabel({ displayName: r.listenerDisplayName, handle: r.listenerHandle })}`,
        detail: [userSecondary(caller), r.status, new Date(r.startedAt).toLocaleString('en-IN')].filter(Boolean).join(' · '),
      };
    }
    case 'recharge': {
      const payer = { displayName: r.userDisplayName, handle: r.userHandle };
      return {
        title: `${formatRupees(r.amountPaise)} · ${userLabel(payer)}`,
        detail: [userSecondary(payer), r.status, r.gatewayOrderId].filter(Boolean).join(' · '),
      };
    }
    case 'payout':
      return {
        title: `${formatRupees(r.amountPaise)} · ${listenerLabel({ displayName: r.listenerDisplayName, handle: r.listenerHandle })}`,
        detail: plain(r.status),
      };
  }
}

/**
 * The header search box: one place to find a listener (by Display Name or
 * @handle), a user (handle, phone last-4, id, referral code), a call, a
 * recharge or a payout, and jump straight to it.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');

  // Search after the admin pauses typing, not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const search = useQuery({
    queryKey: ['admin', 'console', 'search', term],
    queryFn: async () =>
      (await api.get<{ results: SearchResult[] }>('/admin/console/search', { params: { q: term } })).data,
    enabled: term.length >= 2,
    placeholderData: keepPreviousData,
  });

  const results = useMemo(() => (term.length >= 2 ? (search.data?.results ?? []) : []), [search.data, term]);
  const options = results.map((r) => {
    const { title, detail } = searchResultText(r);
    return {
      key: `${r.type}:${r.id}`,
      value: `${r.type}:${r.id}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag color={TYPE_COLOR[r.type]} style={{ width: 72, textAlign: 'center', marginInlineEnd: 0 }}>
            {TYPE_LABEL[r.type]}
          </Tag>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {detail}
            </Typography.Text>
          </div>
        </div>
      ),
    };
  });

  return (
    <AutoComplete
      style={{ flex: 1, maxWidth: 520, minWidth: 0 }}
      value={text}
      options={options}
      popupMatchSelectWidth={420}
      onChange={(v) => setText(v)}
      onSelect={(value: string) => {
        const hit = results.find((r) => `${r.type}:${r.id}` === value);
        setText('');
        setTerm('');
        if (hit) navigate(searchResultLink(hit));
      }}
      notFoundContent={
        term.length >= 2 && !search.isFetching ? (
          <Typography.Text type="secondary">Nothing found for “{term}”</Typography.Text>
        ) : null
      }
    >
      <Input
        allowClear
        aria-label="Search the admin panel"
        prefix={<SearchOutlined />}
        placeholder="Search name, @handle, phone last 4, call / order id, referral code"
      />
    </AutoComplete>
  );
}
